/**
 * datasetResourceAdapter.js — Phase 1 resource-registry adapter for datasets.
 *
 * Wraps the existing useDatasetStore()/findWidgetsUsingDataset() APIs behind
 * the generic resource-adapter shape. It owns no state and no persistence —
 * every method delegates straight through to the existing dataset store (and,
 * for isUsed, the existing datasetValidation helper) so dataset behavior is
 * unchanged. useDatasetStore() is called lazily inside each method rather
 * than once at module scope, since Pinia may not be installed yet when this
 * module is imported/registered.
 *
 * Note on isUsed(uuid, layer): the codebase has no global accessor for the
 * current Konva layer (it lives as instance state on the root App.vue and is
 * passed down as a prop, e.g. DatasetManager.vue's `objectlayer` prop). This
 * adapter therefore accepts `layer` as an explicit second argument and falls
 * back to `false` when it isn't provided, mirroring the existing
 * `props.objectlayer ? findWidgetsUsingDataset(...) : false` fallback already
 * used in DatasetManager.vue.
 */

import { useDatasetStore } from '../datasetStore.js'
import { findWidgetsUsingDataset } from '../../utils/datasetValidation.js'

export const datasetResourceAdapter = {
  type: 'dataset',

  list() {
    return useDatasetStore().all
  },
  get(uuid) {
    return useDatasetStore().getDataset(uuid)
  },
  remove(uuid) {
    return useDatasetStore().removeDataset(uuid)
  },
  duplicate(uuid) {
    return useDatasetStore().duplicateDataset(uuid)
  },
  rename(uuid, name) {
    return useDatasetStore().updateDataset(uuid, { name })
  },
  isUsed(uuid, layer) {
    if (!layer) return false
    return findWidgetsUsingDataset(layer, uuid).length > 0
  },
  getDisplayName(uuid) {
    return useDatasetStore().getDataset(uuid)?.name ?? null
  },
}
